/**
 * Wizard Components - Reusable components for multi-step wizards
 */
import React from "react";
import { cn } from "../../lib/utils";
import { CheckCircle2 } from "lucide-react";

/**
 * WizardContainer - Wrapper for wizard content
 */
export function WizardContainer({ children, className }) {
  return (
    <div className={cn("bg-white rounded-lg border shadow-sm p-6", className)}>
      {children}
    </div>
  );
}

/**
 * WizardStepIndicator - Shows progress through wizard steps
 */
export function WizardStepIndicator({ steps, currentStep, onStepClick, className }) {
  return (
    <div className={cn("flex items-center justify-between", className)}>
      {steps.map((step, index) => {
        const isCompleted = currentStep > step.id;
        const isCurrent = currentStep === step.id;
        const StepIcon = step.icon;
        
        return (
          <React.Fragment key={step.id}>
            <button
              onClick={() => onStepClick && onStepClick(step.id)}
              className={cn(
                "flex flex-col items-center gap-2 transition-all",
                onStepClick ? "cursor-pointer hover:opacity-80" : "cursor-default"
              )}
              disabled={!onStepClick}
            >
              <div
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all",
                  isCompleted
                    ? "bg-emerald-500 border-emerald-500 text-white"
                    : isCurrent
                    ? "bg-sky-700 border-sky-700 text-white"
                    : "bg-white border-slate-300 text-slate-400"
                )}
              >
                {isCompleted ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : StepIcon ? (
                  <StepIcon className="w-5 h-5" />
                ) : (
                  <span className="text-sm font-medium">{step.id}</span>
                )}
              </div>
              <div className="text-center">
                <p
                  className={cn(
                    "text-xs font-medium",
                    isCompleted
                      ? "text-emerald-600"
                      : isCurrent
                      ? "text-sky-700"
                      : "text-slate-400"
                  )}
                >
                  {step.title}
                </p>
                {step.description && (
                  <p className="text-[10px] text-slate-400 hidden sm:block">
                    {step.description}
                  </p>
                )}
                {step.clause && (
                  <span className="text-[9px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded font-mono">
                    {step.clause}
                  </span>
                )}
              </div>
            </button>
            
            {index < steps.length - 1 && (
              <div
                className={cn(
                  "flex-1 h-0.5 mx-2",
                  isCompleted ? "bg-emerald-500" : "bg-slate-200"
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * WizardStep - Container for individual step content
 */
export function WizardStep({ step, currentStep, children }) {
  if (step !== currentStep) return null;
  
  return (
    <div className="animate-in fade-in-0 slide-in-from-right-5 duration-300">
      {children}
    </div>
  );
}

/**
 * WizardNavigation - Previous/Next buttons for wizard
 */
export function WizardNavigation({
  currentStep,
  totalSteps,
  onPrevious,
  onNext,
  onSave,
  onSubmit,
  saving,
  submitting,
  canSubmit = true,
  previousLabel = "Previous",
  nextLabel = "Next",
  saveLabel = "Save Draft",
  submitLabel = "Submit",
  className,
}) {
  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === totalSteps;

  return (
    <div className={cn("flex items-center justify-between pt-6 border-t mt-6", className)}>
      <button
        onClick={onPrevious}
        disabled={isFirstStep}
        className={cn(
          "px-4 py-2 rounded-lg border transition-colors",
          isFirstStep
            ? "border-slate-200 text-slate-300 cursor-not-allowed"
            : "border-slate-300 text-slate-600 hover:bg-slate-50"
        )}
      >
        {previousLabel}
      </button>
      
      <div className="flex gap-2">
        {onSave && (
          <button
            onClick={onSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors"
          >
            {saving ? "Saving..." : saveLabel}
          </button>
        )}
        
        {isLastStep ? (
          onSubmit && (
            <button
              onClick={onSubmit}
              disabled={submitting || !canSubmit}
              className={cn(
                "px-6 py-2 rounded-lg font-medium transition-colors",
                canSubmit
                  ? "bg-sky-700 text-white hover:bg-sky-800"
                  : "bg-slate-300 text-slate-500 cursor-not-allowed"
              )}
            >
              {submitting ? "Submitting..." : submitLabel}
            </button>
          )
        ) : (
          <button
            onClick={onNext}
            className="px-6 py-2 rounded-lg bg-sky-700 text-white font-medium hover:bg-sky-800 transition-colors"
          >
            {nextLabel}
          </button>
        )}
      </div>
    </div>
  );
}
